import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { dashboardApi } from '../api/dashboard.api.js';
import { automationsApi } from '../api/automations.api.js';
import { useAuthStore } from '../store/authStore.js';
import { hasAutomation } from '../utils/tenantFeatures.js';
import { Tabs } from '../components/shared/Tabs.jsx';
import { EmptyState } from '../components/shared/EmptyState.jsx';
import { timeAgo } from '../utils/format.js';
import toast from 'react-hot-toast';
import { callsApi } from '../api/calls.api.js';
import { IconAlert, IconCheckCircle, IconDownload, IconLightning, IconMic, IconPhone, IconRefresh, IconSearch, IconX } from '../components/icons/Icons.jsx';
import { StatCard } from '../components/shared/StatCard.jsx';

const FILTERS = [
  ['all', 'All'],
  ['answered', 'Answered'],
  ['missed', 'Missed'],
  ['busy', 'Busy'],
  ['voicemail', 'Voicemail'],
  ['forwarded', 'Forwarded'],
];

function statusStyle(status) {
  const map = {
    answered: ['var(--success)', 'rgba(34,201,122,.12)'],
    active: ['var(--info)', 'rgba(59,167,255,.12)'],
    ringing: ['var(--warning)', 'rgba(245,166,35,.12)'],
    busy: ['var(--warning)', 'rgba(245,166,35,.12)'],
    missed: ['var(--danger)', 'rgba(239,17,17,.12)'],
    voicemail: ['var(--accent)', 'var(--accentSoft)'],
    forwarded: ['var(--info)', 'rgba(59,167,255,.12)'],
  };
  return map[status] || ['var(--textMuted)', 'var(--surface)'];
}

function Status({ value }) {
  const [color, bg] = statusStyle(value);
  return <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase" style={{ color, background: bg, border: '1px solid var(--border)' }}>{value || 'unknown'}</span>;
}

function formatDuration(seconds = 0) {
  const value = Number(seconds || 0);
  return `${Math.floor(value / 60)}m ${value % 60}s`;
}

function csvCell(value) {
  return `"${String(value ?? '').replaceAll('"', '""')}"`;
}

function downloadCsv(items) {
  const rows = [
    ['Date', 'Contact', 'Phone', 'Direction', 'Status', 'From', 'To', 'Forwarded To', 'Duration Seconds', 'Recording', 'Transcription'],
    ...items.map(call => [
      new Date(call.createdAt).toISOString(),
      call.contact?.name || '',
      call.contact?.phone || '',
      call.direction,
      call.status,
      call.fromNumber || '',
      call.toNumber || '',
      call.forwardedTo || '',
      call.durationSeconds || 0,
      call.recordingUrl ? 'yes' : 'no',
      call.voicemailTranscription || '',
    ]),
  ].map(row => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `calls-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function Detail({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-bold tracking-widest" style={{ color: 'var(--textDim)' }}>{label}</p>
      <div className="mt-1 text-sm font-semibold break-words" style={{ color: 'var(--text)' }}>{children || 'Not available'}</div>
    </div>
  );
}

function startCall(phone) {
  window.dispatchEvent(new CustomEvent('bulk-sms-crm:start-call', { detail: { phone } }));
}

// Missed calls nobody has returned yet, one row per caller.
function Callbacks() {
  const [rows, setRows] = useState(null);
  const load = () => dashboardApi.callbacks(14).then(({ data }) => setRows(data.data || [])).catch(() => setRows([]));
  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);
  if (rows === null) return <div className="card h-40 animate-pulse" />;
  if (!rows.length) {
    return <div className="card"><EmptyState icon={IconCheckCircle} title="Everyone has been called back" text="Missed calls from the last 14 days show here until someone calls the person back." /></div>;
  }
  return (
    <ul className="card divide-y" style={{ borderColor: 'var(--border)' }}>
      {rows.map((row) => (
        <li key={row.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" style={{ borderColor: 'var(--border)' }}>
          <span className="w-10 h-10 rounded-full grid place-items-center flex-shrink-0" style={{ background: 'var(--dangerSoft)', color: 'var(--danger)' }}><IconPhone size={17} /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold" style={{ color: 'var(--text)' }}>
              {row.contact?.name || row.fromNumber || 'Unknown caller'}
              {row.attempts > 1 && <span className="ml-2 status-pill status-pill--warning">Called {row.attempts} times</span>}
            </p>
            <p className="text-sm" style={{ color: 'var(--textMuted)' }}>{row.fromNumber} · {row.status} {timeAgo(row.createdAt)} ago</p>
            {row.voicemailTranscription && <p className="text-sm mt-1 italic" style={{ color: 'var(--text)' }}>“{row.voicemailTranscription}”</p>}
          </div>
          <div className="flex gap-2">
            {row.contact && <Link to={`/contacts/${row.contact.id}`} className="btn-secondary !min-h-[36px] text-xs">Profile</Link>}
            {row.fromNumber && <button type="button" onClick={() => startCall(row.fromNumber)} className="btn-primary !min-h-[36px] text-xs"><IconPhone size={14} /> Call back</button>}
          </div>
        </li>
      ))}
    </ul>
  );
}

// Automations that answer incoming calls (IVR menus and routing).
function CallFlows() {
  const [flows, setFlows] = useState(null);
  useEffect(() => {
    automationsApi.list().then(({ data }) => setFlows((data || []).filter((a) => a.triggerType === 'ivr_call'))).catch(() => setFlows([]));
  }, []);
  if (flows === null) return <div className="card h-40 animate-pulse" />;
  return (
    <div className="space-y-4">
      <div className="card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Call flows are automations that start with an incoming call: keypad menus, routing and follow-up texts. Pick which one is live under Channels → Voice.</p>
        <div className="flex gap-2 flex-shrink-0">
          <Link to="/channels/voice" className="btn-secondary text-sm">Call handling</Link>
          <Link to="/automations?new=1" className="btn-primary text-sm">New call flow</Link>
        </div>
      </div>
      {flows.length === 0 ? (
        <div className="card"><EmptyState icon={IconLightning} title="No call flows yet" text="Start from the Driving School IVR template or build your own keypad menu." /></div>
      ) : (
        <ul className="grid md:grid-cols-2 gap-3">
          {flows.map((flow) => (
            <li key={flow.id}>
              <Link to={`/automations/${flow.id}`} className="card p-4 block hover:border-[var(--borderStrong)]">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-bold truncate" style={{ color: 'var(--text)' }}>{flow.name}</span>
                  <span className={`status-pill status-pill--${flow.status === 'ACTIVE' ? 'success' : 'neutral'}`}>{flow.status === 'ACTIVE' ? 'Live' : flow.status.toLowerCase()}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>
                  {flow.lastRunAt ? `Last answered a call ${timeAgo(flow.lastRunAt)} ago` : 'Not used yet'}
                  {flow.failuresLast7Days ? ` · ${flow.failuresLast7Days} failed this week` : ''}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Calls() {
  const tenant = useAuthStore((s) => s.tenant);
  const role = useAuthStore((s) => s.user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const showFlows = hasAutomation(tenant) && ['ADMIN', 'MANAGER'].includes(role);
  const viewTabs = [
    { key: 'log', label: 'Call log' },
    { key: 'callbacks', label: 'To call back' },
    ...(showFlows ? [{ key: 'flows', label: 'Call flows' }] : []),
  ];
  const view = viewTabs.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'log';
  const setView = (key) => setSearchParams(key === 'log' ? {} : { tab: key }, { replace: true });
  const [filter, setFilter] = useState('all');
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = (quiet = false) => {
    if (!quiet) setLoading(true);
    callsApi.list({ status: filter })
      .then(({ data }) => setData(data))
      .catch(err => toast.error(err.response?.data?.message || 'Could not load calls'))
      .finally(() => { if (!quiet) setLoading(false); });
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => load(true), 20000);
    return () => clearInterval(timer);
  }, [autoRefresh, filter]);

  const items = (data.items || []).filter(call => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [
      call.contact?.name,
      call.contact?.phone,
      call.fromNumber,
      call.toNumber,
      call.direction,
      call.status,
      call.voicemailTranscription,
    ].filter(Boolean).some(value => String(value).toLowerCase().includes(needle));
  });
  const stats = items.reduce((acc, call) => {
    acc.total += 1;
    acc[call.status] = (acc[call.status] || 0) + 1;
    if (call.recordingUrl) acc.recordings += 1;
    return acc;
  }, { total: 0, recordings: 0 });

  return (
    <div className="h-full overflow-y-auto mobile-safe-page">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-8 sm:py-8 space-y-5">
        <div className="page-header !mb-2 flex-col items-start sm:flex-row sm:items-end">
          <div>
            <p className="eyebrow">Voice center</p>
            <h1 className="page-title">Calls</h1>
            <p className="page-subtitle">Every inbound and outbound call, who still needs a call back, and the menus that answer your line.</p>
          </div>
          <div className={`flex flex-wrap gap-2 ${view === 'log' ? '' : 'hidden'}`}>
            <button type="button" onClick={() => setAutoRefresh(v => !v)} className="btn-secondary text-sm" style={autoRefresh ? { color: 'var(--success)', borderColor: 'var(--borderHi)' } : undefined}>
              Auto {autoRefresh ? 'On' : 'Off'}
            </button>
            <button type="button" onClick={() => downloadCsv(items)} disabled={items.length === 0} className="btn-secondary text-sm"><IconDownload size={15} /> Export</button>
            <button type="button" onClick={() => load()} disabled={loading} className="btn-secondary text-sm"><IconRefresh size={15} /> Refresh</button>
          </div>
        </div>

        <Tabs items={viewTabs} value={view} onChange={setView} />
        {view === 'callbacks' && <Callbacks />}
        {view === 'flows' && <CallFlows />}

        {view === 'log' && <>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard label="Visible calls" value={stats.total} icon={IconPhone} />
          <StatCard label="Answered" value={stats.answered || 0} icon={IconCheckCircle} valueColor="var(--success)" />
          <StatCard label="Missed" value={stats.missed || 0} icon={IconAlert} valueColor="var(--danger)" />
          <StatCard label="Recordings" value={stats.recordings || 0} icon={IconMic} />
        </div>

        <div className="relative">
          <IconSearch size={17} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--textDim)' }} />
          <input
            className="input pl-10"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search calls by name, phone, status, or transcript..."
          />
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className="px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap"
              style={{
                background: filter === key ? 'var(--accentSoft)' : 'var(--card)',
                color: filter === key ? 'var(--accent)' : 'var(--textMuted)',
                border: '1px solid var(--border)',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[1,2,3].map(i => <div key={i} className="h-28 rounded-xl animate-pulse" style={{ background: 'var(--card)' }} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl p-10 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <IconPhone size={34} className="mx-auto mb-3" style={{ color: 'var(--accent)' }} />
            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{query ? 'No matching calls' : 'No calls yet'}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>{query ? 'Try another name, phone number, status, or transcript keyword.' : 'When this workspace receives or places calls, they will appear here.'}</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-hidden rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Date','Contact','Direction','Status','Duration','Recording',''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-widest font-bold" style={{ color: 'var(--textDim)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(call => (
                    <tr key={call.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="px-4 py-3" style={{ color: 'var(--textMuted)' }}>{new Date(call.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <p className="font-bold" style={{ color: 'var(--text)' }}>{call.contact?.name || call.fromNumber || call.toNumber || 'Unknown'}</p>
                        <p className="text-xs" style={{ color: 'var(--textMuted)' }}>{call.contact?.phone || call.fromNumber || call.toNumber}</p>
                      </td>
                      <td className="px-4 py-3 capitalize" style={{ color: 'var(--text)' }}>{call.direction}</td>
                      <td className="px-4 py-3"><Status value={call.status} /></td>
                      <td className="px-4 py-3" style={{ color: 'var(--textMuted)' }}>{formatDuration(call.durationSeconds)}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--textMuted)' }}>{call.recordingUrl ? 'Available' : 'None'}</td>
                      <td className="px-4 py-3 text-right"><button type="button" onClick={() => setSelected(call)} className="btn-secondary text-xs">View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 md:hidden">
              {items.map(call => (
                <button key={call.id} type="button" onClick={() => setSelected(call)} className="rounded-xl p-4 text-left" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold truncate" style={{ color: 'var(--text)' }}>{call.contact?.name || call.fromNumber || call.toNumber || 'Unknown'}</p>
                      <p className="text-xs mt-1 truncate" style={{ color: 'var(--textMuted)' }}>{new Date(call.createdAt).toLocaleString()}</p>
                    </div>
                    <Status value={call.status} />
                  </div>
                  <div className="flex gap-4 mt-3 text-xs capitalize" style={{ color: 'var(--textMuted)' }}>
                    <span>{call.direction}</span>
                    <span>{formatDuration(call.durationSeconds)}</span>
                    {call.recordingUrl && <span>Recording</span>}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]" style={{ color: 'var(--textDim)' }}>
                    <span className="truncate">From {call.fromNumber || 'unknown'}</span>
                    <span className="truncate">To {call.toNumber || 'unknown'}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        </>}
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] grid place-items-end sm:place-items-center" onMouseDown={e => e.target === e.currentTarget && setSelected(null)} style={{ background: 'var(--overlay)' }}>
          <div className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl p-5 space-y-5" style={{ background: 'var(--card)', border: '1px solid var(--borderHi)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold" style={{ color: 'var(--text)' }}>Call detail</p>
                <p className="text-xs" style={{ color: 'var(--textMuted)' }}>{new Date(selected.createdAt).toLocaleString()}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="tap-target rounded-xl flex items-center justify-center" style={{ color: 'var(--textMuted)' }}><IconX size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Detail label="Status"><Status value={selected.status} /></Detail>
              <Detail label="Direction">{selected.direction}</Detail>
              <Detail label="From">{selected.fromNumber}</Detail>
              <Detail label="To">{selected.toNumber}</Detail>
              <Detail label="Duration">{formatDuration(selected.durationSeconds)}</Detail>
              <Detail label="Forwarded to">{selected.forwardedTo}</Detail>
            </div>
            {selected.recordingUrl && (
              <div className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <audio className="w-full" controls src={callsApi.recordingUrl(selected.id)} />
                <a className="btn-secondary text-sm mt-3 inline-flex" href={callsApi.recordingUrl(selected.id)} download>
                  <IconDownload size={15} /> Download recording
                </a>
              </div>
            )}
            {selected.voicemailTranscription && (
              <div className="rounded-xl p-3 text-sm" style={{ background: 'var(--surface)', color: 'var(--textMuted)', border: '1px solid var(--border)' }}>
                {selected.voicemailTranscription}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
